"use client";

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";
import {
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  Bell,
  ShieldCheck,
  Smartphone,
  DollarSign,
  Users,
  Calendar,
  FileText,
  UserCheck,
  MessageSquare,
} from "lucide-react";

const features = [
  {
    icon: DollarSign,
    title: "Fee Management",
    description:
      "Collect fees online with multiple payment options. Automated reminders reduce follow-ups by 80%.",
  },
  {
    icon: UserCheck,
    title: "Attendance System",
    description:
      "Digital attendance with biometric support. Parents get instant SMS notifications for absences.",
  },
  {
    icon: Calendar,
    title: "Smart Timetable",
    description:
      "Auto-generate conflict-free timetables for teachers and classes in seconds.",
  },
  {
    icon: GraduationCap,
    title: "Grade & Report Cards",
    description:
      "Manage exam results, generate report cards, and track student progress over time.",
  },
  {
    icon: DollarSign,
    title: "Payroll Management",
    description:
      "Process staff salaries automatically with tax calculations and salary slip generation.",
  },
  {
    icon: MessageSquare,
    title: "Parent Communication",
    description:
      "In-app messaging, SMS alerts, and a parent portal keep parents engaged and informed.",
  },
  {
    icon: FileText,
    title: "Admissions",
    description:
      "Digital admission forms, document collection, and student onboarding, all paperless.",
  },
  {
    icon: BarChart3,
    title: "Analytics Dashboard",
    description:
      "Real-time insights on fees, attendance, academics, and operations in one dashboard.",
  },
  {
    icon: ShieldCheck,
    title: "Role-Based Access",
    description:
      "Teachers, admins, and parents each see only what they need, secure and clean.",
  },
];

const benefits = [
  "Reduce manual admin work by 20+ hours per week",
  "Automated fee reminders improve collection rates",
  "Real-time parent communication reduces queries",
  "Cloud-based, no server setup or IT team required",
  "Android & iOS apps for staff and parents",
  "Affordable pricing built for Indian schools",
];

export default function SchoolManagement() {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <GraduationCap className="w-4 h-4" /> School Management System
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Complete ERP for Schools, 
              <span className="text-blue-200">Built for India</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Manage fees, attendance, timetables, payroll, grades, and parent
              communication, all in one platform. No more spreadsheets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?product=school">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/schedule-demo">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  Book a Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything a School Needs
            </h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              One platform that handles every operation, from admissions to
              results.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card
                key={idx}
                className="border hover:border-blue-200 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="w-11 h-11 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Schools Choose Clynox
              </h2>
              <p className="text-lg text-gray-500 mb-8">
                500+ schools across India trust Clynox to run their daily
                operations, because it just works, even with limited internet.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "500+", label: "Schools Using Clynox" },
                { value: "50K+", label: "Active Users" },
                { value: "98%", label: "Satisfaction Rate" },
                { value: "20h+", label: "Saved Per Week" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 text-center shadow-sm border"
                >
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Modernise Your School?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get started with a free 30-day trial. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?product=school">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact?product=school">
              <Button
                size="lg"
                variant="outline"
                className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
